import { useState } from "react";
import { Gift, Star, Copy, Check, ExternalLink, Bookmark, BookmarkCheck, Clock, TrendingUp, Users, Sparkles, GraduationCap, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useAuth } from "../../context/AuthContext";
import { MOCK_SCHOLARSHIPS, MOCK_PERKS, Scholarship, Perk } from "../../data/mockData";

const TABS = ["SCS Benefits", "Scholarships", "Perks & Deals", "Referral"];

const SCS_BENEFITS = [
  { emoji: "📚", title: "Free SPM Notes",           desc: "Access curated SPM revision notes and past year papers for all subjects.",                    tag: "Available now",  tagColor: "bg-green-100 text-green-700" },
  { emoji: "🌐", title: "Free IELTS Prep Class",    desc: "Complimentary IELTS preparation session after enrollment through SCS Education.",            tag: "After enrollment", tagColor: "bg-violet-100 text-violet-700" },
  { emoji: "🤖", title: "Free AI Skills Class",     desc: "AI fundamentals and prompt engineering class — free for students enrolled through SCS.",      tag: "After enrollment", tagColor: "bg-violet-100 text-violet-700" },
  { emoji: "🎓", title: "Free University Comparison", desc: "Compare courses, fees, entry requirements and intakes across 300+ universities — at no cost.", tag: "Always free",    tagColor: "bg-blue-100 text-blue-700" },
  { emoji: "💸", title: "Fee Rebate Check",         desc: "SCS Education will check if you qualify for any fee rebates or financial support at partner universities.", tag: "On request", tagColor: "bg-orange-100 text-orange-700" },
  { emoji: "💬", title: "Dedicated Counsellor",     desc: "A personal counsellor assigned to your case — for guidance from course selection to enrollment.", tag: "Always free", tagColor: "bg-blue-100 text-blue-700" },
];

const TIER_CONFIG = {
  Bronze:   { next: "Silver",   points: 1000, color: "from-amber-500 to-amber-700",   bar: "bg-amber-500",  emoji: "🥉" },
  Silver:   { next: "Gold",     points: 5000, color: "from-slate-400 to-slate-600",   bar: "bg-slate-400",  emoji: "🥈" },
  Gold:     { next: "Platinum", points: 10000,color: "from-yellow-400 to-amber-500",  bar: "bg-yellow-400", emoji: "🥇" },
  Platinum: { next: null,       points: 10000,color: "from-cyan-400 to-blue-500",     bar: "bg-cyan-400",   emoji: "💎" },
};

function ScholarshipCard({ s }: { s: Scholarship }) {
  const [saved, setSaved] = useState(s.saved);
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`text-[10px] px-2.5 py-1 rounded-full text-white ${
              s.type === "Government" ? "bg-blue-500" :
              s.type === "Corporate" ? "bg-violet-500" :
              s.type === "University" ? "bg-orange-500" : "bg-slate-500"
            }`} style={{ fontWeight: 700 }}>{s.type}</span>
            <span className="text-[11px] text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full" style={{ fontWeight: 700 }}>
              {s.match}% match
            </span>
          </div>
          <h4 className="text-[14px] text-slate-900 leading-snug mb-0.5" style={{ fontWeight: 700 }}>{s.name}</h4>
          <p className="text-[12px] text-slate-500" style={{ fontWeight: 400 }}>{s.provider}</p>
        </div>
        <button onClick={() => setSaved(!saved)}
          className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${saved ? "bg-violet-50 text-violet-600" : "bg-slate-50 text-slate-400 hover:bg-slate-100"}`}>
          {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
        </button>
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[20px]">💰</span>
          <p className="text-[13px] text-slate-800" style={{ fontWeight: 600 }}>{s.amount}</p>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          <p className="text-[12px] text-red-600" style={{ fontWeight: 600 }}>Deadline: {s.deadline}</p>
        </div>
      </div>

      {/* Fields */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {s.fields.map(f => (
          <span key={f} className="text-[11px] text-slate-600 bg-slate-50 border border-slate-100 px-2.5 py-1 rounded-full" style={{ fontWeight: 500 }}>{f}</span>
        ))}
      </div>

      {/* Match bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-[11px] text-slate-500" style={{ fontWeight: 500 }}>Profile match</span>
          <span className="text-[11px] text-green-600" style={{ fontWeight: 700 }}>{s.match}%</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: `${s.match}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className={`h-1.5 rounded-full ${s.match >= 80 ? "bg-green-500" : s.match >= 60 ? "bg-amber-500" : "bg-slate-400"}`}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[12px] flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg transition-all hover:-translate-y-px" style={{ fontWeight: 600 }}>
          Apply Now <ExternalLink className="w-3 h-3" />
        </button>
        <button className="px-4 py-2.5 rounded-xl border border-violet-200 text-violet-700 bg-violet-50 text-[12px] hover:bg-violet-100 transition-colors" style={{ fontWeight: 600 }}>
          Ask Counselor
        </button>
      </div>
    </div>
  );
}

function PerkCard({ perk, points }: { perk: Perk; points: number }) {
  const [redeemed, setRedeemed] = useState(perk.redeemed);
  const canAfford = perk.pointsCost ? points >= perk.pointsCost : true;

  return (
    <div className={`bg-white rounded-3xl border shadow-sm p-5 transition-all duration-200 hover:shadow-md ${redeemed ? "border-green-100 opacity-70" : "border-slate-100 hover:-translate-y-0.5"}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-[24px] flex-shrink-0">
          {perk.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] text-slate-900 truncate" style={{ fontWeight: 700 }}>{perk.brand}</p>
          <p className="text-[11px] text-slate-500" style={{ fontWeight: 400 }}>{perk.category}</p>
        </div>
        {redeemed && (
          <span className="flex items-center gap-1 text-[11px] text-green-600 bg-green-50 px-2 py-1 rounded-full" style={{ fontWeight: 700 }}>
            <Check className="w-3 h-3" /> Used
          </span>
        )}
      </div>
      <p className="text-[13px] text-slate-700 mb-3 leading-snug" style={{ fontWeight: 500 }}>{perk.deal}</p>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3 text-slate-400" />
          <span className="text-[11px] text-slate-400" style={{ fontWeight: 400 }}>Expires {perk.expiresAt}</span>
        </div>
        {perk.pointsCost && (
          <span className={`text-[11px] px-2.5 py-1 rounded-full ${canAfford ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-500"}`} style={{ fontWeight: 700 }}>
            {perk.pointsCost} pts
          </span>
        )}
      </div>
      <button
        disabled={redeemed || (!canAfford && !!perk.pointsCost)}
        onClick={() => setRedeemed(true)}
        className={`w-full py-2.5 rounded-xl text-[12px] flex items-center justify-center gap-2 transition-all ${
          redeemed ? "bg-slate-100 text-slate-400 cursor-not-allowed" :
          !canAfford && perk.pointsCost ? "bg-slate-100 text-slate-400 cursor-not-allowed" :
          "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md hover:shadow-lg hover:-translate-y-px"
        }`} style={{ fontWeight: 600 }}>
        {redeemed ? "Redeemed" : !canAfford && perk.pointsCost ? `Need ${perk.pointsCost - points} more pts` : perk.pointsCost ? `Redeem (${perk.pointsCost} pts)` : "Claim Free"}
      </button>
    </div>
  );
}

function ReferralSection({ points }: { points: number }) {
  const [copied, setCopied] = useState(false);
  const refCode = "AINREFERS2025";

  const copyCode = () => {
    navigator.clipboard.writeText(refCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Hero banner */}
      <div className="relative bg-slate-900 rounded-3xl p-8 overflow-hidden text-center">
        <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-violet-600/20 blur-3xl" />
        <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-orange-500/15 blur-3xl" />
        <div className="relative z-10">
          <span className="text-[40px] mb-3 block">🎁</span>
          <h3 className="text-[24px] text-white mb-2" style={{ fontWeight: 900 }}>Refer & Earn</h3>
          <p className="text-[14px] text-slate-400 mb-6 max-w-sm mx-auto" style={{ fontWeight: 400 }}>
            Share SCS Education with friends. You earn <span className="text-violet-400" style={{ fontWeight: 700 }}>RM 50 + 500 EduPoints</span> for every friend who registers and gets matched.
          </p>
          <div className="flex items-center gap-2 bg-white/10 rounded-2xl px-4 py-3 max-w-xs mx-auto">
            <span className="flex-1 text-[14px] text-white text-center tracking-widest" style={{ fontWeight: 700 }}>{refCode}</span>
            <button onClick={copyCode}
              className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-all">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          {copied && <p className="text-[12px] text-green-400 mt-2" style={{ fontWeight: 500 }}>Copied to clipboard!</p>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { icon: <Users className="w-5 h-5" />, value: "3", label: "Friends referred", color: "text-violet-600 bg-violet-50" },
          { icon: <span className="text-[18px]">💰</span>, value: "RM 150", label: "Cash earned", color: "text-green-600 bg-green-50" },
          { icon: <Star className="w-5 h-5 fill-amber-400 text-amber-400" />, value: "1,500", label: "EduPoints earned", color: "text-amber-600 bg-amber-50" },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 text-center">
            <div className={`w-10 h-10 rounded-xl ${stat.color.split(" ")[1]} flex items-center justify-center mx-auto mb-2 ${stat.color.split(" ")[0]}`}>
              {stat.icon}
            </div>
            <p className="text-[22px] text-slate-900" style={{ fontWeight: 900 }}>{stat.value}</p>
            <p className="text-[11px] text-slate-500" style={{ fontWeight: 400 }}>{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Steps */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
        <h4 className="text-[15px] text-slate-900 mb-5" style={{ fontWeight: 700 }}>How it works</h4>
        <div className="flex flex-col gap-4">
          {[
            { step: "1", text: "Share your referral code or link with a friend who wants to study locally or overseas.", icon: "🔗" },
            { step: "2", text: "Your friend signs up, creates their profile and gets matched with universities.", icon: "📋" },
            { step: "3", text: "Once they get their first counselor session, you both receive your rewards!", icon: "🎉" },
          ].map(s => (
            <div key={s.step} className="flex gap-4 items-start">
              <div className="w-9 h-9 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center flex-shrink-0">
                <span className="text-[16px]">{s.icon}</span>
              </div>
              <div className="flex-1">
                <p className="text-[13px] text-slate-700 leading-relaxed" style={{ fontWeight: 400 }}>
                  <span className="text-violet-700" style={{ fontWeight: 700 }}>Step {s.step}: </span>{s.text}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[13px] shadow-md hover:shadow-lg transition-all" style={{ fontWeight: 600 }}>
            Share via WhatsApp
          </button>
          <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-[13px] hover:bg-slate-50 transition-all" style={{ fontWeight: 600 }}>
            Copy Link
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RewardsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Scholarships");

  if (!user) return null;
  const tier = TIER_CONFIG[user.tier];
  const progressPercent = Math.min((user.points / tier.points) * 100, 100);

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-[28px] text-slate-900 tracking-tight" style={{ fontWeight: 900 }}>Rewards</h1>
        <p className="text-[14px] text-slate-500 mt-1" style={{ fontWeight: 400 }}>
          Scholarships matched to your profile, exclusive partner perks, and your EduPoints balance.
        </p>
      </motion.div>

      {/* Points hero card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className={`relative bg-gradient-to-r ${tier.color} rounded-3xl p-6 lg:p-8 mb-6 overflow-hidden text-white`}>
        <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-black/10 blur-2xl" />
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[24px]">{tier.emoji}</span>
              <span className="text-[12px] text-white/80 bg-white/20 px-3 py-1 rounded-full uppercase tracking-widest" style={{ fontWeight: 700 }}>
                {user.tier} Member
              </span>
            </div>
            <p className="text-[48px] text-white leading-none mb-1" style={{ fontWeight: 900 }}>
              {user.points.toLocaleString()}
            </p>
            <p className="text-[14px] text-white/70" style={{ fontWeight: 500 }}>EduPoints</p>
          </div>
          <div className="flex-1 max-w-xs w-full">
            {tier.next && (
              <>
                <div className="flex justify-between mb-2">
                  <span className="text-[12px] text-white/80" style={{ fontWeight: 500 }}>{user.tier}</span>
                  <span className="text-[12px] text-white/80" style={{ fontWeight: 500 }}>{tier.next}</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2 mb-2">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 1.2, delay: 0.3 }}
                    className="h-2 rounded-full bg-white" />
                </div>
                <p className="text-[12px] text-white/70" style={{ fontWeight: 400 }}>
                  {(tier.points - user.points).toLocaleString()} more points to {tier.next}
                </p>
              </>
            )}
            <div className="grid grid-cols-3 gap-2 mt-4">
              {[
                { label: "Earned this month", value: "350" },
                { label: "Redeemed", value: "300" },
                { label: "Available", value: user.points.toString() },
              ].map(stat => (
                <div key={stat.label} className="bg-white/20 rounded-xl p-2 text-center">
                  <p className="text-[14px] text-white" style={{ fontWeight: 800 }}>{stat.value}</p>
                  <p className="text-[9px] text-white/70 leading-tight" style={{ fontWeight: 500 }}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Earn more banner */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 mb-6">
        <p className="text-[13px] text-slate-700 mb-3" style={{ fontWeight: 700 }}>🚀 Earn more EduPoints</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { action: "Complete profile", pts: "+200 pts", done: true },
            { action: "Submit application", pts: "+150 pts", done: true },
            { action: "Upload document", pts: "+50 pts ea.", done: false },
            { action: "Refer a friend", pts: "+500 pts", done: false },
          ].map((item, i) => (
            <div key={i} className={`p-3 rounded-2xl border text-center ${item.done ? "bg-green-50 border-green-100" : "bg-slate-50 border-slate-100"}`}>
              {item.done && <Check className="w-4 h-4 text-green-500 mx-auto mb-1" />}
              <p className="text-[11px] text-slate-700" style={{ fontWeight: 600 }}>{item.action}</p>
              <p className={`text-[12px] mt-0.5 ${item.done ? "text-green-600" : "text-violet-600"}`} style={{ fontWeight: 700 }}>{item.pts}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-white rounded-2xl border border-slate-100 p-1.5 shadow-sm mb-6 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2.5 rounded-xl text-[13px] transition-all ${tab === t ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
            style={{ fontWeight: tab === t ? 700 : 500 }}>
            {t}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        {tab === "SCS Benefits" && (
          <motion.div key="scs-benefits" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SCS_BENEFITS.map(b => (
              <div key={b.title} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-2xl bg-violet-50 flex items-center justify-center text-[22px] flex-shrink-0">
                    {b.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-slate-900 mb-1" style={{ fontWeight: 700 }}>{b.title}</p>
                    <span className={`inline-flex text-[10px] px-2.5 py-0.5 rounded-full ${b.tagColor}`} style={{ fontWeight: 700 }}>{b.tag}</span>
                  </div>
                </div>
                <p className="text-[12px] text-slate-500 leading-relaxed" style={{ fontWeight: 400 }}>{b.desc}</p>
                <a href="https://wa.me/601112870256" target="_blank" rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-[12px] text-violet-600 hover:text-violet-700 transition-colors" style={{ fontWeight: 600 }}>
                  Ask about this <MessageCircle className="w-3 h-3" />
                </a>
              </div>
            ))}
          </motion.div>
        )}
        {tab === "Scholarships" && (
          <motion.div key="scholarships" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="grid sm:grid-cols-2 gap-5">
            {MOCK_SCHOLARSHIPS.map(s => <ScholarshipCard key={s.id} s={s} />)}
          </motion.div>
        )}
        {tab === "Perks & Deals" && (
          <motion.div key="perks" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MOCK_PERKS.map(p => <PerkCard key={p.id} perk={p} points={user.points} />)}
          </motion.div>
        )}
        {tab === "Referral" && (
          <motion.div key="referral" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <ReferralSection points={user.points} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}